import { Alert, Flex, Heading, InlineList, Text, View } from "@instructure/ui";
import c3 from "c3";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import RichTextEditor from "react-rte";
import PublicTextArea from "../../components/CustomTextArea/PublicTextArea";
import ChartColors from "../../utils/ChartColors";
import { sort } from "../../utils/utils";

const PublicCanvasJuriedAssessmentChart = ({
  measure,
  filters,
  canvasView,
  jaView,
  tenantId,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(t("assessment.masteryAvg"));
  const graphData = useMemo(() => {
    if (measure.results.canvas) {
      measure.results.canvas[canvasView][0].forEach((term) => {
        for (const key in term) {
          if (key !== "name") {
            if (term[key] !== null) {
              term[key] = Number(term[key].toFixed(2));
            }
          }
        }
      });
      measure.results.canvas[canvasView][1] = sort(
        measure.results.canvas[canvasView][1]
      );
      return measure.results.canvas[canvasView];
    } else {
      return [[], []];
    }
  }, [measure, canvasView]);
  const jaGraphData = useMemo(() => {
    if (measure.results.ja) {
      measure.results.ja[jaView][0].forEach((term) => {
        for (const key in term) {
          if (key !== "name") {
            if (term[key] !== null) {
              term[key] = Number(term[key].toFixed(2));
            }
          }
        }
      });
      measure.results.ja[jaView][1] = sort(measure.results.ja[jaView][1]);
      return measure.results.ja[jaView];
    } else {
      return [[], []];
    }
  }, [measure, jaView]);

  useEffect(() => {
    setTitle(
      filters.masteryResults === "true"
        ? t("assessment.masteryAvg")
        : t("assessment.gradeAvg")
    );
  }, [graphData]);

  useEffect(() => {
    if (graphData) {
      c3.generate({
        bindto: document.getElementById(
          "public-assessment-chart-" + measure.id
        ),
        padding: { bottom: 25, left: 50 },
        color: { pattern: ChartColors },
        legend: { show: false },
        size: { height: 240 },
        data: {
          type: "bar",
          json: graphData[0],
          keys: { x: "name", value: graphData[1] },
        },
        zoom: { enabled: true },
        axis: {
          x: {
            type: "category", // this needed to load string x value
          },
          y: {
            label: {
              text:
                filters.masteryResults === "false"
                  ? t("dashboard.avgAssignmentGrade")
                  : t("dashboard.percentAchievedMastery"),
              position: "outer-middle",
            },
            min: 5,
            max: 100,
          },
        },
        tooltip: {
          order: function (data1, data2) {
            return sort([data1, data2], (d) => d.name);
          },
        },
      });
    }
  }, [graphData, measure.id]);

  useEffect(() => {
    if (jaGraphData) {
      c3.generate({
        bindto: document.getElementById(
          "public-assessment-chart-ja-" + measure.id
        ),
        padding: { bottom: 25, left: 50 },
        color: { pattern: ChartColors },
        legend: { show: false },
        size: { height: 240 },
        data: {
          type: "bar",
          json: jaGraphData[0],
          keys: { x: "name", value: jaGraphData[1] },
        },
        zoom: { enabled: true },
        axis: {
          x: {
            type: "category", // this needed to load string x value
          },
          y: {
            label: {
              text:
                filters.masteryResults === "false"
                  ? t("dashboard.avgAssignmentGrade")
                  : t("dashboard.percentAchievedMastery"),
              position: "outer-middle",
            },
            min: 5,
            max: 100,
          },
        },
      });
    }
  }, [jaGraphData, measure.id]);

  return (
    <Flex wrap="wrap" gap="small">
      <Flex.Item size="100%">
        <Heading level="h3">{t("assessment.canvasResults")}</Heading>
      </Flex.Item>
      {measure.description && (
        <Flex.Item padding="0 0 0 large">
          <RichTextEditor
            className="readOnlyClass"
            readOnly
            value={RichTextEditor.createValueFromString(
              measure.description,
              "html"
            )}
          />
        </Flex.Item>
      )}
      {graphData[0].length === 0 ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Alert variant="info">{t("results.noResults")}</Alert>
        </Flex.Item>
      ) : (
        <>
          <Flex.Item size="100%" padding="medium 0 0 large">
            <View textAlign="center">
              <Heading level="h4">{title}</Heading>
              <div
                id={"public-assessment-chart-" + measure.id}
                style={{ margin: "10px 0 -20px 0", width: "100%" }}
              />
            </View>
          </Flex.Item>
          <Flex.Item size="100%" padding="0 0 medium xx-large">
            <View as="div" textAlign="center">
              <InlineList delimiter="pipe" isUnstyled>
                {graphData[1].map((category, index) => (
                  <InlineList.Item key={index}>
                    <div
                      style={{
                        display: "inline-block",
                        height: 10,
                        width: 10,
                        marginRight: 8,
                        backgroundColor: ChartColors[index],
                      }}
                    />
                    <Text as="span" size="small">
                      {category}
                    </Text>
                  </InlineList.Item>
                ))}
              </InlineList>
            </View>
          </Flex.Item>
        </>
      )}
      <Flex.Item size="100%">
        <Heading level="h3">{t("assessment.canvasJuriedResults")}</Heading>
      </Flex.Item>
      {jaGraphData[0].length === 0 ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Alert variant="info">{t("results.noResults")}</Alert>
        </Flex.Item>
      ) : (
        <>
          <Flex.Item size="100%" padding="small 0 0 large">
            <View textAlign="center">
              <Heading level="h4">{title}</Heading>
              <div
                id={"public-assessment-chart-ja-" + measure.id}
                style={{ margin: "10px 0 -20px 0", width: "100%" }}
              />
            </View>
          </Flex.Item>
          <Flex.Item size="100%" padding="0 0 0 xx-large">
            <View as="div" textAlign="center">
              <InlineList delimiter="pipe" isUnstyled>
                {jaGraphData[1].map((category, index) => (
                  <InlineList.Item key={index}>
                    <div
                      style={{
                        display: "inline-block",
                        height: 10,
                        width: 10,
                        marginRight: 8,
                        backgroundColor: ChartColors[index],
                      }}
                    />
                    <Text as="span" size="small">
                      {category}
                    </Text>
                  </InlineList.Item>
                ))}
              </InlineList>
            </View>
          </Flex.Item>
        </>
      )}
      <Flex.Item size="100%" padding="0 0 0 large">
        <PublicTextArea
          id={measure.id}
          label={<Heading level="h4">{t("juriedAssessment.findings")}</Heading>}
          type="public-findings"
          tenantId={tenantId}
        />
      </Flex.Item>
      <Flex.Item size="100%" padding="0 0 0 large">
        <PublicTextArea
          id={measure.id}
          label={
            <Heading level="h4">
              {t("juriedAssessment.recommendations")}
            </Heading>
          }
          type="public-recommendations"
          tenantId={tenantId}
        />
      </Flex.Item>
    </Flex>
  );
};

export default PublicCanvasJuriedAssessmentChart;
